import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link } from "wouter";

// ── Design tokens: exact match to LittlebirdEightWays brand ─────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
  green: "oklch(0.38 0.09 155)",
};

// ── Data ─────────────────────────────────────────────────────────────────────
interface QuickWin {
  num: number;
  title: string;
  when: string;
  how: string[];
  principle: string;
}

const WINS: QuickWin[] = [
  {
    num: 1,
    title: "Know What Littlebird Is (and Isn't)",
    when: "Use this before you install, so your expectations match what the tool actually does.",
    how: [
      "Littlebird has a full chat interface, but unlike a generic AI assistant, it already knows your work",
      "You don't paste context, explain your projects, or catch it up. It's already there",
      "Think of it less as a chatbot and more as a thinking partner with a full memory of your day",
      "The chat gets smarter over time as it learns more about how you work",
    ],
    principle: "Context changes everything, even in a chat window.",
  },
  {
    num: 2,
    title: "Install and Let It Learn",
    when: "Use this in your first session so the tool can start building context immediately.",
    how: [
      "Download Littlebird at littlebird.ai (free to start, no invite required)",
      "Open it and let it run in the background while you work normally",
      "Connect your calendar and email for deeper context (optional but recommended)",
      "Don't try to use it heavily on day one. Let it observe for a full workday first",
    ],
    principle: "Patience in setup pays back in relevance.",
  },
  {
    num: 3,
    title: "Prepare for Any Meeting",
    when: "Use this when you're about to meet someone and want to walk in with full context.",
    how: [
      "Before the meeting, open Littlebird and ask it to research who you're meeting",
      "It will pull from your email history, their public background, and your calendar notes",
      "Ask for 3–5 talking points and one idea for collaboration",
      "Review it in two minutes. You'll walk in more prepared than if you'd spent twenty",
    ],
    principle: "Preparation shouldn't cost you the time it saves.",
  },
  {
    num: 4,
    title: "Capture and Close the Loop",
    when: "Use this when follow-up falls through the cracks after meetings.",
    how: [
      "Let Littlebird run during your meeting; it transcribes automatically",
      "After the meeting, ask it to draft your follow-up email and list your commitments",
      "Review and send. The whole loop closes in under five minutes",
      "Stay in the same conversation thread for prep, notes, and follow-up",
    ],
    principle: "The loop that closes itself removes the task you always forget.",
  },
  {
    num: 5,
    title: "Ask for Your Weekly Productivity Mirror",
    when: "Use this when you're not sure what you accomplished, or feel behind when you're not.",
    how: [
      "At the end of the week, ask Littlebird to summarize what you worked on",
      "Ask which areas got the most time and which got the least",
      "Ask for one thing to streamline and one thing to improve next week",
      "Read it without judgment, then decide what to change",
    ],
    principle: "You can't improve what you can't see clearly.",
  },
  {
    num: 6,
    title: "Build a Routine Once, Run It Forever",
    when: "Use this when a recurring task keeps pulling your attention away from higher-value work.",
    how: [
      "Open Routines in Littlebird and create a scheduled task: daily, weekly, or monthly",
      "Start with one: a daily briefing, an inbox triage, or a weekly summary",
      "Set it, confirm the schedule, and let Littlebird handle it from there",
      "You don't need to open it every day. Just know the resource is building",
    ],
    principle: "Automation earns you back the time to think.",
  },
  {
    num: 7,
    title: "Use Hummingbird for In-Context Help",
    when: "Use this when switching windows breaks your flow.",
    how: [
      "Press the Hummingbird shortcut to open a floating window over whatever you're working on",
      "Ask a question, get a summary, or request a draft, without leaving your current app",
      "Littlebird answers using the context it already has from your screen and history",
      "Close it and keep working. No tab switching, no context loss",
    ],
    principle: "The best tool is the one that doesn't interrupt the work.",
  },
  {
    num: 8,
    title: "Connect Your Tools for Deeper Context",
    when: "Use this when Littlebird's answers feel surface-level and you want more precision.",
    how: [
      "Go to integrations and connect Gmail, Google Calendar, or the tools you use most",
      "Littlebird supports 90+ integrations, but you don't need to connect all of them",
      "Start with email and calendar: those two alone dramatically improve context quality",
      "Integrations are optional; Littlebird works without them via screen reading alone",
    ],
    principle: "More signal, less noise. Connect only what you'll actually use.",
  },
  {
    num: 9,
    title: "Control What Littlebird Sees",
    when: "Use this when you want the benefits of always-on context without giving up control.",
    how: [
      "Open settings and customize which apps Littlebird observes, and which it ignores",
      "Pause context collection anytime with one click. It stops immediately",
      "Delete recent data (last hour, last day, or all of it) whenever you choose",
      "Littlebird never sees minimized apps, private browser windows, or password fields",
    ],
    principle: "Trust requires transparency and the ability to say stop.",
  },
  {
    num: 10,
    title: "Let Littlebird Run While You Focus Elsewhere",
    when: "Use this when you're ready to stop managing the tool and let it work for you.",
    how: [
      "Stop checking Littlebird. Start reaching for it only when you need it",
      "Trust that it's been building context even when you haven't asked anything",
      "Use it as a reference, not a dashboard. Query it like memory, not a filing system",
      "Pair it with your other tools: let this layer handle context while they handle execution",
    ],
    principle: "The tools that ask the least often give the most back.",
  },
];

const GROUPS = [
  { label: "Quick Wins 1 – 3", wins: WINS.slice(0, 3) },
  { label: "Quick Wins 4 – 6", wins: WINS.slice(3, 6) },
  { label: "Quick Wins 7 – 10", wins: WINS.slice(6, 10) },
];

// ── Chevron icon ──────────────────────────────────────────────────────────────
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{
        transition: "transform 0.25s ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
        color: C.muted,
      }}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Win card ──────────────────────────────────────────────────────────────────
function WinCard({ win, defaultOpen = false }: { win: QuickWin; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${open ? C.terracottaMid : C.border}`,
        borderLeft: `3px solid ${open ? C.terracotta : C.terracottaMid}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        boxShadow: open
          ? "0 6px 24px oklch(0.32 0.08 255 / 0.10)"
          : hovered
          ? "0 2px 10px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          padding: "1.1rem 1.4rem",
          background: open ? C.terracottaLight : "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.2s ease",
        }}
      >
        {/* Number badge */}
        <span
          style={{
            width: 32,
            height: 32,
            minWidth: 32,
            borderRadius: "50%",
            background: open ? C.terracotta : C.terracottaLight,
            color: open ? C.white : C.terracotta,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 13,
            fontWeight: 700,
            transition: "background 0.2s ease, color 0.2s ease",
            flexShrink: 0,
          }}
        >
          {win.num}
        </span>
        <span
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 16,
            fontWeight: 600,
            color: C.espresso,
            flex: 1,
            lineHeight: 1.35,
          }}
        >
          {win.title}
        </span>
        <ChevronIcon open={open} />
      </button>

      {/* Expanded body */}
      {open && (
        <div
          style={{
            padding: "1.25rem 1.4rem 1.5rem",
            borderTop: `1px solid ${C.terracottaMid}`,
            background: C.white,
          }}
        >
          {/* When */}
          <div style={{ marginBottom: "1.1rem" }}>
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: C.terracotta,
                marginBottom: "0.35rem",
              }}
            >
              When This Helps
            </p>
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 14,
                color: C.muted,
                fontStyle: "italic",
                lineHeight: 1.7,
              }}
            >
              {win.when}
            </p>
          </div>

          {/* How */}
          <div style={{ marginBottom: "1.25rem" }}>
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: C.terracotta,
                marginBottom: "0.65rem",
              }}
            >
              How
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              {win.how.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  {/* Step counter */}
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      minWidth: 20,
                      borderRadius: "50%",
                      background: C.linen,
                      border: `1px solid ${C.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.muted,
                      marginTop: 2,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <p
                    style={{
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                      fontSize: 14,
                      color: C.body,
                      lineHeight: 1.7,
                      margin: 0,
                    }}
                  >
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Smart Stack Principle */}
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderLeft: `3px solid ${C.terracotta}`,
              borderRadius: 6,
              padding: "0.75rem 1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 9,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: C.terracotta,
                display: "block",
                marginBottom: "0.3rem",
              }}
            >
              Smart Stack Principle
            </span>
            <span
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 14,
                color: C.espresso,
                fontStyle: "italic",
                lineHeight: 1.5,
              }}
            >
              {win.principle}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function LittlebirdQuickWinsPage() {
  return (
    <GuideShell
      context={"Littlebird guide"}
      backHref={"/resources/littlebird"}
      backLabel={"Back to Littlebird"}
    >

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "3.5rem 1.5rem 5rem" }}>

        {/* Hero header */}
        <div style={{ textAlign: "center", marginBottom: "3rem", paddingBottom: "2.5rem", borderBottom: `1px solid ${C.border}` }}>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: C.terracotta,
              marginBottom: "1rem",
            }}
          >
            Your Smart Stack Quick Wins
          </p>
          <h1
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              fontWeight: 700,
              color: C.espresso,
              lineHeight: 1.2,
              marginBottom: "0.75rem",
            }}
          >
            Littlebird
          </h1>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 18,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: 1.5,
            }}
          >
            Always on. Always ready. Already there.
          </p>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 20,
              padding: "0.45rem 1.1rem",
            }}
          >
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                color: C.terracotta,
                letterSpacing: "0.04em",
              }}
            >
              Remember everything. Ask anything.
            </span>
          </div>
        </div>

        {/* Intro card */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "1.75rem 2rem",
            marginBottom: "2.75rem",
          }}
        >
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 22,
              fontWeight: 700,
              color: C.espresso,
              marginBottom: "0.3rem",
            }}
          >
            The Memory Layer
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 14,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.1rem",
            }}
          >
            Your AI that already knows your work.
          </p>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15,
              color: C.body,
              lineHeight: 1.8,
              marginBottom: "0.9rem",
            }}
          >
            Most AI tools ask something of you: context, a prompt, an explanation of what you've been doing. Littlebird asks almost nothing. It runs quietly on your Mac or PC, reading what's on your screen and listening to your meetings, building a private memory of your work without requiring manual input. When you need something, it's already there.
          </p>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15,
              color: C.body,
              lineHeight: 1.8,
              marginBottom: "1.5rem",
            }}
          >
            This guide shows how to use that memory layer deliberately, not as a passive recorder, but as an active thinking partner that earns its place in your stack by knowing you.
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: C.terracotta,
                marginBottom: "0.6rem",
              }}
            >
              How to Use This Guide
            </p>
            <p
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 16,
                fontWeight: 600,
                color: C.espresso,
                marginBottom: "0.5rem",
              }}
            >
              This is not a setup checklist.
            </p>
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 14,
                color: C.body,
                lineHeight: 1.75,
                marginBottom: "0.75rem",
              }}
            >
              You don't need to implement everything here. Each Quick Win is designed for a specific moment when:
            </p>
            {[
              "you're tired of re-explaining yourself to AI tools",
              "context is slipping through the cracks",
              "you want the work done before you even ask",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start", marginBottom: "0.45rem" }}>
                <span
                  style={{
                    color: C.terracotta,
                    fontWeight: 700,
                    fontSize: 15,
                    flexShrink: 0,
                    marginTop: 0,
                    lineHeight: 1.6,
                  }}
                >
                  –
                </span>
                <p
                  style={{
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                    fontSize: 14,
                    color: C.body,
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {item}
                </p>
              </div>
            ))}
            <p
              style={{
                fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                fontSize: 14,
                color: C.body,
                lineHeight: 1.75,
                marginTop: "0.85rem",
              }}
            >
              Start with the win that solves the friction you feel right now. Skip the rest.
            </p>
            <p
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 15,
                fontWeight: 600,
                color: C.espresso,
                marginTop: "0.6rem",
                fontStyle: "italic",
              }}
            >
              Continuity is the feature, not convenience.
            </p>
          </div>
        </div>

        {/* Quick Win groups */}
        {GROUPS.map((group) => (
          <div key={group.label} style={{ marginBottom: "2.75rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.1rem",
              }}
            >
              <div style={{ height: 1, flex: 1, background: C.border }} />
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: C.terracotta,
                  whiteSpace: "nowrap",
                }}
              >
                {group.label}
              </p>
              <div style={{ height: 1, flex: 1, background: C.border }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
              {group.wins.map((win, i) => (
                <WinCard
                  key={win.num}
                  win={win}
                  defaultOpen={i === 0 && group.wins[0].num === 1}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Closing note */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.terracotta}`,
            borderRadius: 10,
            padding: "2rem 2.25rem",
            marginBottom: "2.5rem",
          }}
        >
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: 22,
              fontWeight: 700,
              color: C.espresso,
              marginBottom: "0.5rem",
            }}
          >
            A Note on Always-On
          </h2>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 15,
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "1.1rem",
              lineHeight: 1.7,
            }}
          >
            Littlebird works best when you stop thinking about it.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "0.5rem" }}>
            It's not the tool you open to start your day.
          </p>
          <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "1.25rem" }}>
            It's the one that was already running before you got there.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1.75rem" }}>
            {["Stay in control.", "Let it remember.", "Reach for it when it matters."].map((line) => (
              <p
                key={line}
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 16,
                  fontWeight: 600,
                  color: C.espresso,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                {line}
              </p>
            ))}
          </div>
          <div
            style={{
              background: C.terracottaLight,
              border: `1px solid ${C.terracottaMid}`,
              borderRadius: 8,
              padding: "1.1rem 1.4rem",
            }}
          >
            <p
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: 15,
                color: C.espresso,
                fontStyle: "italic",
                lineHeight: 1.65,
                marginBottom: "0.6rem",
              }}
            >
              "Littlebird is the one tool in my stack that works hardest when I'm not looking at it."
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 13, fontWeight: 700, color: C.terracotta, marginBottom: "0.1rem" }}>
              Your Smart Stack
            </p>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
              Your Smart Stack
            </p>
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            href="/resources/littlebird"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13,
              color: C.muted,
              fontWeight: 600,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: C.white,
              transition: "border-color 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.terracotta;
              e.currentTarget.style.color = C.terracotta;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.color = C.muted;
            }}
          >
            ← Littlebird Resources
          </Link>
          <a
            href="https://try.littlebird.ai/caren-glasser"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              fontSize: 13,
              color: C.white,
              fontWeight: 700,
              textDecoration: "none",
              padding: "0.65rem 1.3rem",
              background: C.terracotta,
              borderRadius: 8,
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "0.85")
            }
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) =>
              (e.currentTarget.style.opacity = "1")
            }
          >
            Try Littlebird →
          </a>
        </div>
      </main>

      <footer
        style={{
          borderTop: `1px solid ${C.border}`,
          padding: "1.5rem",
          textAlign: "center",
        }}
      >
        <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>
          Your Smart Stack Quick Wins &nbsp;·&nbsp;{" "}
          <a
            href="https://carenglasser.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.muted, textDecoration: "underline" }}
          >
            carenglasser.com
          </a>
        </p>
      </footer>

      {/* Responsive */}
      <style>{`
        @media (max-width: 600px) {
          main { padding: 2rem 1rem 4rem !important; }
        }
      `}</style>
    </GuideShell>
  );
}
